<template>
  <gmap-map
    ref="map"
    :center="center"
    :options="{
      clickableIcons: false,
      mapTypeControl: false,
      streetViewControl: false,
    }"
    :zoom="zoom"
    map-type-id="roadmap"
    style="width: 100%; height: 100%"
    @idle="idle"
    @zoom_changed="zoomHandler"
  >
    <gmap-marker
      v-if="pinnedAddress"
      :position="pinnedAddress.geometry.location"
      :icon="icon"
    />

    <gmap-cluster :max-zoom="13" :styles="clusterStyles" :zoom-on-click="true">
      <marker-component
        v-for="(dealer, index) in dealersWithLocation"
        :key="index"
        :dealer="dealer"
        :is-active="
          activeDealer && dealer.haendlernummer === activeDealer.haendlernummer
        "
        @setActiveDealer="setActiveDealer"
        @closeInfoWindow="closeInfoWindow"
      />
    </gmap-cluster>

    <gmap-info-window
      v-if="activeDealer"
      :position="{
        lat: activeDealer.latitude,
        lng: activeDealer.longitude,
      }"
      :options="{
        pixelOffset: { width: 0, height: -30 },
      }"
    >
      <div class="relative mb-4 flex items-center">
        <h1 class="text-base font-bold">
          {{ activeDealer.name1 }}
        </h1>

        <button
          class="border border-grey-100 rounded overflow-hidden ml-auto hover:shadow flex-shrink-0"
          @click="closeInfoWindow"
        >
          <span class="sr-only">schließen</span>
          <svg
            aria-hidden="true"
            focusable="false"
            width="16"
            height="16"
            viewBox="0 0 16 16"
            version="1.1"
            xmlns="http://www.w3.org/2000/svg"
            xmlns:xlink="http://www.w3.org/1999/xlink"
          >
            <rect
              id="Rectangle-Copy-5"
              fill="#F6F6F6"
              x="0"
              y="0"
              width="16"
              height="16"
            />
            <g stroke-linecap="round" stroke-width="1.5">
              <line x1="4" y1="12" x2="12" y2="4" stroke="black" />
              <line x1="12" y1="12" x2="4" y2="4" stroke="black" />
            </g>
          </svg>
        </button>
      </div>

      <div class="space-y-4">
        <p>
          {{ activeDealer.strasse }}<br />
          {{ activeDealer.plz }} {{ activeDealer.ort }}
        </p>

        <div class="space-x-4">
          <a
            :href="computeRouteLink()"
            target="_blank"
            rel="noopener"
            class="button text-red"
          >
            {{ activeDealer ? 'Route planen' : 'Route zum nächsten Händler' }}
          </a>
          <a
            v-if="activeDealer"
            class="button text-red"
            :href="`#${activeDealer.haendlernummer}`"
          >
            <span>Details</span>
          </a>
        </div>
      </div>
      <span class="info-window-arrow"></span>
    </gmap-info-window>
  </gmap-map>
</template>

<script>
import MarkerComponent from './MarkerComponent.vue';
import { googleIsLoaded } from '../tools';
import icons from '../icons';

const color = '#e31721';

export default {
  components: {
    MarkerComponent,
  },

  props: {
    dealers: {
      type: Array,
      default: () => [],
    },
    pinnedAddress: {
      type: Object,
      required: false,
    },
    activeDealer: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      clusterStyles: null,
      newPinnedAddress: false,
      icon: null,
      zoom: 7,
      center: { lat: 47.7, lng: 13.3 },
      bounds: null,
    };
  },

  computed: {
    visibleMarkers() {
      return this.dealers.filter(dealer => {
        if (dealer && dealer.latitude && dealer.longitude) {
          return this.bounds.contains({
            lat: dealer.latitude,
            lng: dealer.longitude,
          });
        }
        return false;
      });
    },

    nearestDealer() {
      return null;
      // return this.$store.getters.nearestDealer;
    },

    locationTitle() {
      return this.geoLocation ? 'Ihr Standort' : 'Ihr angegebener Ort';
    },

    dealersWithLocation() {
      return this.dealers.filter(dealer => dealer.latitude && dealer.longitude);
    },
  },

  watch: {
    pinnedAddress(value) {
      if (value) {
        this.zoom = 14;
        this.newPinnedAddress = true;
        const bounds = value.geometry.viewport;

        this.$refs.map.$mapObject.fitBounds(bounds);
        this.$refs.map.$mapObject.panToBounds(bounds);
      }
    },
  },

  mounted() {
    this.clusterStyles = [
      {
        width: 54,
        height: 54,
        url: `data:image/svg+xml;charset=UTF-8;base64,${btoa(
          icons.mapCluster,
        )}`,
        textColor: color,
      },
      {
        width: 54,
        height: 54,
        url: `data:image/svg+xml;charset=UTF-8;base64,${btoa(
          icons.mapCluster,
        )}`,
        textColor: color,
      },
      {
        width: 54,
        height: 54,
        url: `data:image/svg+xml;charset=UTF-8;base64,${btoa(
          icons.mapCluster,
        )}`,
        textColor: color,
      },
    ];

    googleIsLoaded().then(() => {
      this.fitBounds();
      this.icon = {
        url: `data:image/svg+xml;charset=UTF-8;base64,${btoa(
          icons.pinnedAddress,
        )}`,
        scaledSize: new google.maps.Size(24, 24),
      };
    });
  },

  methods: {
    setActiveDealer(value) {
      this.$emit('setActiveDealer', value);
    },

    closeInfoWindow() {
      this.$emit('closeInfoWindow');
    },

    fitBounds() {
      const bounds = new google.maps.LatLngBounds();
      this.dealers.forEach(dealer => {
        if (dealer.location) {
          bounds.extend({
            lat: dealer.latitude,
            lng: dealer.longitude,
          });
        }
      });
    },

    zoomHandler(value) {
      if (value < this.zoom) {
        this.closeInfoWindow();
      }
      this.zoom = value;
    },

    idle() {
      const bounds = this.$refs.map.$mapObject.getBounds();

      if (bounds) {
        this.bounds = bounds;

        if (this.newPinnedAddress) {
          if (this.dealers.length && this.visibleMarkers.length < 1) {
            this.$refs.map.$mapObject.setZoom(
              this.$refs.map.$mapObject.getZoom() - 1,
            );
          } else {
            this.newPinnedAddress = false;
          }
        } else if (this.dealers.length && this.visibleMarkers.length === 0) {
          this.$refs.map.$mapObject.setZoom(
            this.$refs.map.$mapObject.getZoom() - 1,
          );
        }
      }
    },

    computeRouteLink() {
      const googleMapsUrl = 'https://www.google.com/maps';

      const start = this.activeDealer;

      const params = [{ daddr: `${start.strasse}, ${start.plz} ${start.ort}` }];

      if (this.pinnedAddress) {
        params[0].saddr = this.pinnedAddress.formatted_address;
      }

      return encodeURI(
        `${googleMapsUrl}?${params.map(param =>
          Object.entries(param)
            .map(([key, value]) => `${key}=${value}`)
            .join('&'),
        )}`,
      );
    },
  },
};
</script>
